import type {
    SpeechLanguagePreference,
    SpeechLocale
} from "./SpeechEngine.js";

const PERSIAN_SCRIPT = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff]/u;
const FRENCH_MARKERS = /(?:[àâçéèêëîïôùûüÿœæ]|\b(?:avec|bonjour|dans|des|est|et|français|je|la|le|les|nous|pas|pour|que|qui|une|vous)\b)/giu;
const ENGLISH_MARKERS = /\b(?:and|are|for|from|hello|is|not|of|the|this|to|we|with|you|your)\b/giu;

function countMatches(text: string, pattern: RegExp): number {
    return text.match(pattern)?.length ?? 0;
}

/** Detects the three languages supported by Dino, with a safe teaching-language fallback. */
function detectSpeechLocale(
    text: string,
    fallback: SpeechLocale = "fr-FR"
): SpeechLocale {
    if (PERSIAN_SCRIPT.test(text)) {
        return "fa-IR";
    }

    const frenchScore = countMatches(text, FRENCH_MARKERS);
    const englishScore = countMatches(text, ENGLISH_MARKERS);

    if (englishScore > frenchScore && englishScore > 0) {
        return "en-US";
    }

    if (frenchScore > 0) {
        return "fr-FR";
    }

    return fallback;
}

function resolveSpeechLocale(
    text: string,
    preference: SpeechLanguagePreference,
    fallback: SpeechLocale = "fr-FR"
): SpeechLocale {
    return preference === "auto"
        ? detectSpeechLocale(text, fallback)
        : preference;
}

function normalizeSpeechText(text: string): string {
    return text.replace(/\s+/gu, " ").trim();
}

/** Splits long content into utterances that remain reliable across browsers. */
function splitSpeechText(rawText: string, maxLength = 220): string[] {
    if (!Number.isFinite(maxLength) || maxLength < 1) {
        throw new RangeError("maxLength must be a positive number.");
    }

    const text = normalizeSpeechText(rawText);
    if (!text) {
        return [];
    }

    const sentences = text.match(/[^.!?؟]+[.!?؟]+|[^.!?؟]+$/gu) ?? [text];
    const chunks: string[] = [];
    let current = "";

    const push = (value: string) => {
        const clean = value.trim();
        if (clean) {
            chunks.push(clean);
        }
    };

    for (const sentence of sentences) {
        const clean = sentence.trim();
        if (!clean) continue;

        if (clean.length > maxLength) {
            push(current);
            current = "";
            let partial = "";
            for (const word of clean.split(/\s+/u)) {
                const candidate = partial ? `${partial} ${word}` : word;
                if (partial && candidate.length > maxLength) {
                    push(partial);
                    partial = word;
                } else {
                    partial = candidate;
                }
            }
            push(partial);
            continue;
        }

        const candidate = current ? `${current} ${clean}` : clean;
        if (current && candidate.length > maxLength) {
            push(current);
            current = clean;
        } else {
            current = candidate;
        }
    }

    push(current);
    return chunks;
}

export {
    detectSpeechLocale,
    normalizeSpeechText,
    resolveSpeechLocale,
    splitSpeechText
};
